import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-4 text-left",
  "data-testid": "pin-step-1"
}
const _hoisted_2 = { class: "w-full sm:max-w-[280px]" }
const _hoisted_3 = {
  key: 0,
  class: "flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-white p-3",
  "data-testid": "pin-current-position"
}
const _hoisted_4 = { class: "font-mono text-xs text-gray-600" }
const _hoisted_5 = { class: "rounded-xl border border-gray-100 bg-white p-4" }
const _hoisted_6 = { class: "flex items-end gap-2" }
const _hoisted_7 = { class: "min-w-0 flex-1" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", { class: "mt-2 text-xs text-gray-500" }, " Busque pelo nome como aparece no Google Maps. O resultado traz o ponto do prédio, não do endereço. ", -1)
const _hoisted_9 = {
  key: 0,
  class: "mt-2 text-sm text-negative",
  "data-testid": "pin-search-error"
}
const _hoisted_10 = {
  key: 1,
  class: "mt-3 flex flex-col gap-2"
}
const _hoisted_11 = ["onClick", "data-testid"]
const _hoisted_12 = { class: "text-sm font-semibold text-gray-800" }
const _hoisted_13 = { class: "text-xs text-gray-500" }
const _hoisted_14 = {
  key: 2,
  class: "mt-3 text-sm text-gray-500",
  "data-testid": "pin-search-empty"
}
const _hoisted_15 = { class: "rounded-xl border border-gray-100 bg-white p-4" }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm font-semibold text-gray-800" }, "Não achou?", -1)
const _hoisted_17 = { class: "mt-3 flex flex-col gap-3 sm:flex-row sm:items-end" }
const _hoisted_18 = { class: "flex min-w-0 flex-1 items-end gap-2" }
const _hoisted_19 = { class: "min-w-0 flex-1" }
const _hoisted_20 = {
  class: "text-xs text-gray-500",
  "data-testid": "pin-registered-address"
}

import { computed, ref } from 'vue'
import { mdiMagnify } from '@mdi/js'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { getApiErrorMessage } from '@/utils/apiError'
import type { PinConfidence, PlaceCandidate } from '@/Network/Services/PlacesService'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import { HEALTH_UNIT_TYPE_OPTIONS } from '../../unitType'
import type { UnitGeolocationApi } from '../../composables/useUnitGeolocation'

const PLACES_SEARCH_FALLBACK =
  'Não foi possível buscar o estabelecimento. Use o endereço do cadastro ou cole o link do Google Maps.'


export default /*#__PURE__*/_defineComponent({
  __name: 'StepFind',
  props: {
    geo: {},
    registeredAddress: {}
  },
  emits: ["next"],
  setup(__props: any, { emit }) {

const props = __props;



/** O caminho principal é o "Continuar" do rodapé; o duplo clique é só um atalho. */


const { notifyErrorMessage } = useErrorToast()

const query = ref('')
const candidates = ref<PlaceCandidate[]>([])
const selectedId = ref<string | null>(null)
const isSearching = ref(false)
const isGeocoding = ref(false)
const hasSearched = ref(false)
const searchError = ref('')

/** Só enquanto nenhum candidato foi buscado: depois disso o destaque é o selecionado. */
const currentPointLabel = computed(() => {
  const point = props.geo.currentPoint.value
  if (!point || !props.geo.foundLocation.value || candidates.value.length) return ''
  return `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`
})

function confidenceLabel(confidence: PinConfidence): string {
  return confidence === 'BUILDING' ? 'Prédio identificado' : 'Endereço aproximado'
}

async function runSearch(): Promise<void> {
  const text = query.value.trim()
  if (!text) return

  isSearching.value = true
  searchError.value = ''
  try {
    // Enviesa pelo ponto atual quando já existe um: unidade em edição costuma
    // estar na cidade certa mesmo com o pino no lugar errado.
    candidates.value = await Network.places.searchText(
      text,
      props.geo.currentPoint.value ?? undefined
    )
    selectedId.value = null
  } catch (e) {
    // Funil canônico: a mensagem do service (que já aponta os planos B) ou o
    // fallback — nunca o texto técnico de um erro cru.
    const message = getApiErrorMessage(e, PLACES_SEARCH_FALLBACK)
    // Inline e no toast: o erro do Places manda o usuário para os planos B logo abaixo.
    searchError.value = message
    notifyErrorMessage(message)
    candidates.value = []
  } finally {
    hasSearched.value = true
    isSearching.value = false
  }
}

/** O geocode do endereço do cadastro é uma ida à rede — o botão precisa mostrar isso. */
async function useRegisteredAddress(): Promise<void> {
  if (!props.registeredAddress) return

  isGeocoding.value = true
  try {
    await props.geo.useRegisteredAddress(props.registeredAddress)
  } finally {
    isGeocoding.value = false
  }
}

function select(candidate: PlaceCandidate): void {
  selectedId.value = candidate.id
  props.geo.setFromPlace(candidate)
}

return (_ctx: any,_cache: any) => {
  const _component_v_select = _resolveComponent("v-select")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_v_select, {
        label: "* Tipo de unidade",
        variant: "outlined",
        density: "comfortable",
        "hide-details": "auto",
        items: _unref(HEALTH_UNIT_TYPE_OPTIONS),
        "model-value": _ctx.geo.unitType.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_ctx.geo.setUnitType($event))),
        "data-testid": "pin-type-select"
      }, null, 8, ["items", "model-value"])
    ]),
    (currentPointLabel.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(LabelBadge, {
            size: "small",
            icon: null,
            color: "blue",
            label: "Posição atual"
          }),
          _createElementVNode("span", _hoisted_4, _toDisplayString(currentPointLabel.value), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          _createVNode(GenericInput, {
            label: "Buscar estabelecimento no Google",
            "model-value": query.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (query.value = $event)),
            onKeyup: _cache[2] || (_cache[2] = _withKeys(($event: any) => (runSearch()), ["enter"])),
            width: "100%",
            placeholder: "Ex: UBS Darlene Fernandes Capim Grosso",
            "hide-details": "",
            "data-testid": "pin-search-input"
          }, null, 8, ["model-value"])
        ]),
        _createVNode(FilledButton, {
          class: "mb-1 flex-none",
          text: "Buscar",
          width: "110px",
          height: "56px",
          "prepend-icon": _unref(mdiMagnify),
          disabled: !query.value.trim(),
          loading: isSearching.value,
          onClick: _cache[3] || (_cache[3] = ($event: any) => (runSearch())),
          "data-testid": "pin-search-button"
        }, null, 8, ["prepend-icon", "disabled", "loading"])
      ]),
      _hoisted_8,
      (searchError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(searchError.value), 1))
        : _createCommentVNode("", true),
      (candidates.value.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(candidates.value, (candidate, index) => {
              return (_openBlock(), _createElementBlock("li", {
                key: candidate.id
              }, [
                _createElementVNode("button", {
                  type: "button",
                  class: _normalizeClass(["w-full rounded-lg border p-3 text-left transition hover:border-[#2F7F91]", 
              candidate.id === selectedId.value
                ? 'border-[#2F7F91] bg-primary5'
                : 'border-gray-100 bg-white'
            ]),
                  onClick: ($event: any) => (select(candidate)),
                  onDblclick: _cache[4] || (_cache[4] = ($event: any) => (emit('next'))),
                  "data-testid": `pin-candidate-${index}`
                }, [
                  _createElementVNode("p", _hoisted_12, _toDisplayString(candidate.name), 1),
                  _createElementVNode("p", _hoisted_13, _toDisplayString(candidate.address), 1),
                  _createVNode(LabelBadge, {
                    class: "mt-2",
                    size: "small",
                    icon: null,
                    color: candidate.confidence === 'BUILDING' ? 'green' : 'yellow',
                    label: confidenceLabel(candidate.confidence)
                  }, null, 8, ["color", "label"])
                ], 42, _hoisted_11)
              ]))
            }), 128))
          ]))
        : (hasSearched.value && !isSearching.value && !searchError.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_14, " Nenhum estabelecimento encontrado para essa busca. "))
          : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_15, [
      _hoisted_16,
      _createElementVNode("div", _hoisted_17, [
        _createVNode(OutlinedButton, {
          text: "Usar o endereço do cadastro",
          width: "auto",
          height: "56px",
          class: "!w-auto !px-4 text-sm",
          disabled: !_ctx.registeredAddress || isGeocoding.value,
          loading: isGeocoding.value,
          onClick: _cache[5] || (_cache[5] = ($event: any) => (useRegisteredAddress())),
          "data-testid": "pin-use-address-button"
        }, null, 8, ["disabled", "loading"]),
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("div", _hoisted_19, [
            _createVNode(GenericInput, {
              label: "Colar link do Google Maps",
              "model-value": _ctx.geo.googleMapsLink.value,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_ctx.geo.setGoogleMapsLink($event))),
              width: "100%",
              placeholder: "Ex: https://maps.app.goo.gl/...",
              "hide-details": "",
              "data-testid": "pin-maps-link-input"
            }, null, 8, ["model-value"])
          ]),
          _createVNode(FilledButton, {
            class: "flex-none",
            text: "Ir",
            width: "80px",
            height: "56px",
            disabled: !_ctx.geo.googleMapsLink.value,
            loading: _ctx.geo.isLoadingLink.value,
            onClick: _cache[7] || (_cache[7] = ($event: any) => (_ctx.geo.applyMapsLink())),
            "data-testid": "pin-apply-link-button"
          }, null, 8, ["disabled", "loading"])
        ])
      ])
    ]),
    _createElementVNode("p", _hoisted_20, " Endereço do cadastro: " + _toDisplayString(_ctx.registeredAddress || '—'), 1)
  ]))
}
}

})